import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
} from '@angular/core';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import { PK_ICONS, type PkIconName } from './pk-icons';

function escapeAttr(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('"', '&quot;').replaceAll('<', '&lt;');
}

/**
 * 공용 아이콘 — lucide IconData를 직접 SVG로 렌더한다 (규장각 dc910b84 패턴).
 *
 * 크기는 두 겹: 상자는 1lh(줄 상자와 겹쳐 정렬 담당), 그림은 1em(글자와 같은 크기).
 * 색은 currentColor 상속. 기본 aria-hidden이며, 텍스트 없는 아이콘 전용 버튼에서만
 * label을 준다. 아이콘+텍스트를 담는 컨테이너는 flex여야 한다 (전역 .btn 참고).
 *
 * innerHTML로 주입한 SVG에는 스코프 속성이 없어 ViewEncapsulation.None +
 * pk-icon 태그 네임스페이스로 스타일을 건다 (::ng-deep 금지 환경 대안).
 */
@Component({
  selector: 'pk-icon',
  standalone: true,
  template: `<span class="pk-icon__svg" [innerHTML]="svgHtml()"></span>`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    /* 어떤 아이콘인지 DOM에서 읽는 용도 (e2e: 메뉴·탭 아이콘 일치 가드) */
    '[attr.data-icon]': 'name()',
    '[attr.aria-hidden]': 'label() === null ? "true" : null',
    '[attr.role]': 'label() === null ? null : "img"',
    '[attr.aria-label]': 'label()',
    '[style.--pk-icon-size]': 'sizeVar()',
    '[style.--pk-icon-box]': 'sizeVar()',
  },
  styles: `
    pk-icon {
      /* 기본값은 CSS에 둔다 — 인라인 스타일로 걸면 소비자 CSS와 @supports 폴백이 죽는다 */
      --pk-icon-size: 1em; /* 그림 = 글자 크기 */
      --pk-icon-box: 1lh; /* 상자 = 줄 높이 */
      display: inline-flex;
      align-items: center;
      justify-content: center;
      vertical-align: top; /* 상자가 줄 높이와 같으므로 위를 맞추면 그림이 줄 한가운데 */
      width: var(--pk-icon-size);
      height: var(--pk-icon-box);
      line-height: inherit; /* 1lh 계산에 부모 line-height 상속 필수 — 덮지 말 것 */
      color: inherit;
      flex-shrink: 0;
    }

    @supports not (height: 1lh) {
      pk-icon {
        --pk-icon-box: 1.5em;
      }
    }

    pk-icon .pk-icon__svg {
      display: inline-flex;
      width: var(--pk-icon-size);
      height: var(--pk-icon-size);
      line-height: 0;
    }

    pk-icon .pk-icon__svg > svg {
      display: block;
      width: 100%;
      height: 100%;
    }
  `,
})
export class PkIcon {
  private readonly sanitizer = inject(DomSanitizer);

  readonly name = input.required<PkIconName>();
  /** 미지정 시 CSS 기본값(상자 1lh · 그림 1em), 지정 시 px 고정 */
  readonly size = input<number | undefined>(undefined);
  readonly strokeWidth = input(1.75);
  /** 텍스트 없는 아이콘 전용 버튼에서만 준다 (aria-label) */
  readonly label = input<string | null>(null);

  protected readonly sizeVar = computed(() => {
    const size = this.size();
    return size === undefined ? null : `${size}px`; // null → 인라인 스타일 제거
  });

  protected readonly svgHtml = computed<SafeHtml>(() => {
    const data = PK_ICONS[this.name()];
    if (data === undefined) {
      // 미등록 이름은 던지지 않는다 — 매핑 누락으로 뷰 전체가 죽는 것을 막는다
      console.warn(`[pk-icon] 등록되지 않은 아이콘: ${String(this.name())}`);
      return this.sanitizer.bypassSecurityTrustHtml('');
    }
    const inner = data
      .map(
        ([tag, attrs]) =>
          `<${tag} ${Object.entries(attrs)
            .filter((entry): entry is [string, string | number] => entry[1] !== undefined)
            .map(([key, value]) => `${key}="${escapeAttr(String(value))}"`)
            .join(' ')}/>`,
      )
      .join('');
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" ` +
      `stroke="currentColor" stroke-width="${escapeAttr(String(this.strokeWidth()))}" ` +
      `stroke-linecap="round" stroke-linejoin="round" focusable="false">${inner}</svg>`;
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  });
}
